import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const toList = (v) => Array.isArray(v) ? v : String(v || '').replace(/^\[|\]$/g, '').split(',').map(s => s.replace(/^['"]|['"]$/g, '').trim()).filter(Boolean);
const EMPTY = { name: '', firm_name: '', email: '', phone: '', designation: '', location: '', website: '', investor_type: '', investment_stage: '', preferred_therapeutic_areas: '', geographic_interest: '', description: '', notes: '' };

export const InvestorFormDialog = ({ open, onOpenChange, investor, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(investor ? { ...EMPTY, ...investor, preferred_therapeutic_areas: toList(investor.preferred_therapeutic_areas).join(', ') } : EMPTY);
  }, [open, investor]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!(form.firm_name || form.name).trim()) return toast.error('Firm or contact name is required');
    if (!form.email.trim()) return toast.error('Email is required');
    setSaving(true);
    const payload = {
      name: form.name.trim() || form.firm_name.trim(), firm_name: form.firm_name.trim(), email: form.email.trim(), phone: form.phone,
      designation: form.designation, location: form.location, website: form.website, investor_type: form.investor_type,
      investment_stage: form.investment_stage, preferred_therapeutic_areas: toList(form.preferred_therapeutic_areas),
      geographic_interest: form.geographic_interest, description: form.description, notes: form.notes,
    };
    try {
      const { data } = investor
        ? await axios.put(`${API}/investors/${investor.id}`, payload, { withCredentials: true })
        : await axios.post(`${API}/investors`, payload, { withCredentials: true });
      onSaved?.(data);
      onOpenChange(false);
      toast.success(investor ? 'Investor updated' : 'Investor added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const F = ({ k, label, placeholder, type = 'text' }) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input type={type} value={form[k] || ''} onChange={set(k)} placeholder={placeholder} className="h-8 text-sm" data-testid={`investor-form-${k}`} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="investor-form-dialog">
        <DialogHeader><DialogTitle>{investor ? 'Edit investor' : 'Add investor'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {F({ k: "firm_name", label: "Firm *", placeholder: "e.g. Rhythm VC" })}
          {F({ k: "name", label: "Contact name", placeholder: "Partner / principal" })}
          {F({ k: "email", label: "Email *", placeholder: "partner@firm.com", type: "email" })}
          {F({ k: "phone", label: "Phone" })}
          {F({ k: "designation", label: "Designation", placeholder: "Partner" })}
          {F({ k: "location", label: "Location", placeholder: "United States" })}
          {F({ k: "website", label: "Website", placeholder: "https://" })}
          {F({ k: "investor_type", label: "Investor type", placeholder: "VC · Corporate VC · Family office · PE" })}
          {F({ k: "investment_stage", label: "Investment stages", placeholder: "Seed, Series A, Growth (comma-separated)" })}
          {F({ k: "geographic_interest", label: "Geographic interest", placeholder: "Global · US · EU · Asia" })}
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Therapeutic areas (comma-separated)</Label>
          <Input value={form.preferred_therapeutic_areas} onChange={set('preferred_therapeutic_areas')} placeholder="Oncology, CNS, Rare disease" className="h-8 text-sm" data-testid="investor-form-preferred_therapeutic_areas" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Thesis / description</Label>
          <Textarea value={form.description} onChange={set('description')} rows={3} className="text-sm" data-testid="investor-form-description" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Internal notes</Label>
          <Textarea value={form.notes} onChange={set('notes')} rows={2} className="text-sm" data-testid="investor-form-notes" />
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={save} disabled={saving} data-testid="investor-form-save">
            {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} {investor ? 'Save' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
